import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { Drawer } from './ui/Drawer';
import { TanTable } from './TanTable';
import api from '../services/api';

interface HistoryRecord {
  recno: number;
  dbtable: string;
  keyvalue: string;
  datetime: string;
  userid: string;
  colname: string;
  oldval: string;
  newval: string;
  type: string;
  otherid: string;
}

interface HistoryResponse {
  count: number;
  records: HistoryRecord[];
}

export interface HistoryDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  /** Full API path to fetch history from. null = drawer is empty / not fetching. */
  endpoint: string | null;
  title: string;
  tableDisplayNames?: Record<string, string>;
  columnDisplayNames?: Record<string, string>;
  /** Label for a record-specific detail identifier, such as a cost line number. */
  otherIdLabel?: string;
}

const formatDateTime = (isoString: string): string => {
  if (!isoString) return '';
  const date = new Date(isoString);
  return date.toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
};

export const HistoryDrawer: React.FC<HistoryDrawerProps> = ({
  isOpen,
  onClose,
  endpoint,
  title,
  tableDisplayNames = {},
  columnDisplayNames = {},
  otherIdLabel = 'Other ID',
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [historyData, setHistoryData] = useState<HistoryResponse | null>(null);
  // The drawer is a native <dialog> (top layer); TanTable's filter/menu popovers
  // portal to document.body by default and would render behind it. Mount them
  // into the dialog instead.
  const contentRef = useRef<HTMLDivElement>(null);
  const [portalEl, setPortalEl] = useState<HTMLElement | null>(null);
  useEffect(() => {
    if (isOpen) setPortalEl((contentRef.current?.closest('dialog') as HTMLElement | null) ?? null);
  }, [isOpen]);

  const fetchHistory = useCallback(async () => {
    if (!endpoint) return;
    setLoading(true);
    setError(null);
    try {
      const response = await api.get<HistoryResponse>(endpoint);
      setHistoryData(response.data);
    } catch (err: unknown) {
      console.error('Failed to fetch history:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch history');
    } finally {
      setLoading(false);
    }
  }, [endpoint]);

  useEffect(() => {
    if (isOpen && endpoint) {
      fetchHistory();
    }
  }, [isOpen, endpoint, fetchHistory]);

  const columns = useMemo<ColumnDef<HistoryRecord>[]>(
    () => [
      {
        accessorKey: 'datetime',
        header: 'Date/Time',
        cell: (info) => formatDateTime(info.getValue() as string),
        size: 160,
      },
      {
        accessorKey: 'userid',
        header: 'User',
        cell: (info) => {
          const record = info.row.original;
          return (
            <div title={record.type || record.userid}>
              {record.userid}
              {record.type && (
                <div className="text-xs text-muted truncate">
                  {record.type}
                </div>
              )}
            </div>
          );
        },
        size: 180,
      },
      {
        accessorKey: 'dbtable',
        header: 'Table',
        cell: (info) => {
          const val = info.getValue() as string;
          return tableDisplayNames[val] ?? val;
        },
        size: 120,
      },
      {
        accessorKey: 'otherid',
        header: otherIdLabel,
        size: 80,
      },
      {
        accessorKey: 'colname',
        header: 'Field',
        cell: (info) => {
          const val = info.getValue() as string;
          return columnDisplayNames[val] ?? val;
        },
        size: 140,
      },
      {
        accessorKey: 'oldval',
        header: 'Old Value',
        cell: (info) => (info.getValue() as string) || '-',
        size: 150,
      },
      {
        accessorKey: 'newval',
        header: 'New Value',
        cell: (info) => (info.getValue() as string) || '-',
        size: 150,
      },
    ],
    [tableDisplayNames, columnDisplayNames, otherIdLabel]
  );

  const drawerTitle = (
    <div className="flex items-center gap-2">
      <span>{title}</span>
      {historyData && (
        <span className="text-sm text-muted">
          ({historyData.count} records)
        </span>
      )}
      <button
        onClick={fetchHistory}
        disabled={loading}
        className="p-1 text-muted hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
        title="Refresh"
      >
        <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
      </button>
    </div>
  );

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={drawerTitle}
      width="50vw"
      position="left"
    >
      <div ref={contentRef} className="h-full flex flex-col">
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 m-4">
            <p className="text-red-700 dark:text-red-400">{error}</p>
          </div>
        )}
        <TanTable
          data={historyData?.records || []}
          columns={columns}
          loading={loading}
          portalContainer={portalEl}
          searchPlaceholder="Search history..."
          fillContainer={true}
          enableSearch={true}
          enableReset={true}
          enableShowHideCols={true}
          enableColumnFilters={true}
          defaultSorting={[{ id: 'datetime', desc: true }]}
        />
      </div>
    </Drawer>
  );
};

export default HistoryDrawer;
